import { useState } from 'react';
import { canShareFiles, copyPending, downloadFile, legacyCopy, pdfFile, sendOnWhatsApp, shareLink, stopSharing } from '../../../lib/share';
import { Button } from '../../ui/Button/Button';
import { MenuDropdown } from '../../ui/Menu/Menu';
import { Modal } from '../../ui/Modal/Modal';
import { useToast } from '../../ui/Toast/Toast';

/** Shows the link when the browser would not let us copy it, with a button to try again. */
function LinkDialog({ link, onClose }: { link: string | null; onClose: () => void }) {
  const toast = useToast();
  const copyAgain = async () => {
    if (!link) return;
    let ok = false;
    try {
      if (window.isSecureContext && navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) ok = legacyCopy(link);
    if (ok) {
      toast.success('Link copied');
      onClose();
    } else {
      toast.error('Copy is blocked here. Select the link and press Ctrl+C.');
    }
  };
  return (
    <Modal
      open={!!link}
      onClose={onClose}
      title="PDF link"
      description="Anyone with this link can open this PDF."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => link && window.open(link, '_blank', 'noopener')}>Open link</Button>
          <Button leftIcon="copy" onClick={copyAgain} data-autofocus>Copy link</Button>
        </>
      }
    >
      <input
        readOnly
        value={link ?? ''}
        aria-label="PDF link"
        onFocus={(e) => e.target.select()}
        className="h-10 w-full rounded-lg border border-line bg-navy-50 px-3 font-mono text-xs text-navy-900 outline-none focus:border-brand-500"
      />
    </Modal>
  );
}

/**
 * "Share" button for an invoice or quotation PDF.
 * kind: 'invoices' | 'quotations'; docNo names the file; message is the WhatsApp / share text.
 * shared: whether a public link is already on (shows "Stop sharing link"); onChange reloads the page data.
 */
export interface ShareMenuProps {
  kind: 'invoices' | 'quotations';
  id: string;
  /** Names the PDF file */
  docNo: string;
  phone?: string;
  /** WhatsApp / share sheet text */
  message: string;
  /** A public link is on (shows "Stop sharing link") */
  shared?: boolean;
  onChange?: () => void;
  label?: string;
}

export function ShareMenu({ kind, id, docNo, phone, message, shared, onChange, label = 'Share' }: ShareMenuProps) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [manualLink, setManualLink] = useState<string | null>(null);

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    try {
      await fn();
    } catch (err) {
      const e = err as Error;
      if (e?.name !== 'AbortError') toast.error(e.message || 'Could not share');
    } finally {
      setBusy(false);
    }
  };

  const sharePdf = () =>
    run(async () => {
      const file = await pdfFile(kind, id, docNo);
      if (navigator.canShare?.({ files: [file] })) {
        try {
          await navigator.share({ files: [file], title: docNo, text: message });
          return;
        } catch (err) {
          if ((err as Error).name === 'AbortError') return; // closed the share sheet
        }
      }
      // Sharing files isn't possible here: save the PDF so it can be attached by hand.
      downloadFile(file);
      toast.info('PDF downloaded. Attach it in WhatsApp or email.');
    });

  // Must stay synchronous up to copyPending(): the copy has to start inside the click.
  const copyLink = () =>
    run(async () => {
      const { text, copied } = await copyPending(shareLink(kind, id));
      onChange?.();
      if (copied) toast.success('Link copied. Anyone with it can open this PDF.');
      else setManualLink(text);
    });

  const items = [
    canShareFiles() && { label: 'Share PDF…', icon: 'send', description: 'WhatsApp, email and other apps', onClick: sharePdf },
    {
      label: 'Send on WhatsApp',
      icon: 'phone',
      description: phone ? `To ${phone}, with a PDF link` : 'With a PDF link',
      onClick: () =>
        run(async () => {
          await sendOnWhatsApp(kind, id, phone, message);
          onChange?.();
        }),
    },
    { label: 'Copy PDF link', icon: 'copy', onClick: copyLink },
    { label: 'Download PDF', icon: 'download', onClick: () => run(async () => downloadFile(await pdfFile(kind, id, docNo))) },
    shared && { divider: true },
    shared && {
      label: 'Stop sharing link',
      icon: 'eyeOff',
      danger: true,
      onClick: () =>
        run(async () => {
          await stopSharing(kind, id);
          toast.success('Link turned off. Old links no longer open.');
          onChange?.();
        }),
    },
  ].filter(Boolean);

  return (
    <>
      <MenuDropdown width={260} trigger={<Button variant="outline" leftIcon="send" loading={busy}>{label}</Button>} items={items} />
      <LinkDialog link={manualLink} onClose={() => setManualLink(null)} />
    </>
  );
}

export default ShareMenu;
